import React, { forwardRef, useId } from 'react';
import { cn } from '../../lib/utils';

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string | boolean;
  description?: string;
  required?: boolean;
  icon?: React.ReactNode;
  rightSection?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'unstyled';
}

const sizeClasses = {
  sm: 'h-7 text-13',
  md: 'h-8 text-13',
  lg: 'h-9 text-sm'
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      error,
      description,
      required,
      icon,
      rightSection,
      size = 'md',
      variant = 'default',
      className = '',
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const errorMessage = typeof error === 'string' ? error : error ? 'This field has an error' : '';

    const inputClasses = cn(
      'flex w-full rounded-md border border-border bg-transparent px-2.5 py-1.5 text-13 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground-subtle transition-colors duration-200 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0 hover:border-muted-foreground/30 disabled:cursor-not-allowed disabled:opacity-50',
      sizeClasses[size],
      icon && 'pl-10',
      rightSection && 'pr-10',
      error && 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/40',
      variant === 'unstyled' && 'border-0',
      className
    );

    return (
      <div className="w-full space-y-1">
        {label && (
          <label htmlFor={id} className="block text-11 font-medium text-muted-foreground">
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </label>
        )}
        {description && (
          <p className="text-11 text-muted-foreground">{description}</p>
        )}
        <div className="relative">
          {icon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={id}
            className={inputClasses}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
            {...props}
          />
          {rightSection && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              {rightSection}
            </div>
          )}
        </div>
        {errorMessage && (
          <p id={`${id}-error`} className="mt-1.5 text-13 text-destructive">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';